import React, { ReactElement } from 'react';
import { makeStyles } from '@mui/styles';
import { IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PreviewIcon from '@mui/icons-material/Preview';
import { colors, theme } from '../../theme';
import { ToolTip } from '../shared/tooltip/tooltip';

const color = colors();
const themes = theme().typography;

export type DocumentRowType = {
	id: string;
	documentType: string;
	documentName: string;
	uploadDate: string;
	updatedBy: string;
	documentURL?: string;
};

type CertificateDocumentsTableType = {
	rows: DocumentRowType[];
	allowDelete: boolean;
	onDownload: (row: DocumentRowType) => void;
	onPreview: (row: DocumentRowType) => void;
	onDelete: (row: DocumentRowType) => void;
};

export const CertificateDocumentsTable = ({
	rows,
	allowDelete,
	onDownload,
	onPreview,
	onDelete,
}: CertificateDocumentsTableType): ReactElement => {
	const classes = useStyles();

	return (
		<TableContainer className={classes.table}>
			<Table stickyHeader>
				<TableHead>
					<TableRow>
						<TableCell width="20%">Document Type</TableCell>
						<TableCell width="34%">Document Name</TableCell>
						<TableCell width="18%">Date of Upload</TableCell>
						<TableCell width="20%">Updated By</TableCell>
						<TableCell width="8%" align="right"></TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{rows.map((row) => (
						<TableRow key={row.id || `${row.documentType}-${row.documentName}`}>
							<TableCell>{row.documentType}</TableCell>
							<TableCell className={classes.documentName} title={row.documentName}>
								{row.documentName.length > 61 ? `${row.documentName.slice(0, 61)}...` : row.documentName}
							</TableCell>
							<TableCell className={classes.uploadDate}>{row.uploadDate}</TableCell>
							<TableCell className={classes.updatedBy}>{row.updatedBy}</TableCell>
							<TableCell>
								<div className={classes.actions}>
									<ToolTip tooltipText="Download">
										<span>
											<IconButton size="small" id="download-document" onClick={() => onDownload(row)}>
												<FileDownloadOutlinedIcon className={classes.iconButton} />
											</IconButton>
										</span>
									</ToolTip>
									<ToolTip tooltipText="View">
										<span>
											<IconButton size="small" id="preview-document" onClick={() => onPreview(row)}>
												<PreviewIcon className={classes.iconButton} />
											</IconButton>
										</span>
									</ToolTip>
									{allowDelete && (
										<ToolTip tooltipText="Delete">
											<span>
												<IconButton size="small" id="delete-document" onClick={() => onDelete(row)}>
													<DeleteOutlinedIcon className={classes.iconButton} />
												</IconButton>
											</span>
										</ToolTip>
									)}
								</div>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</TableContainer>
	);
};

const useStyles = makeStyles(() => ({
	table: {
		maxHeight: '530px',
		overflowY: 'auto',
		borderBottom: `1px solid ${color.neutral[4]}`,
		'& .MuiTableCell-head': {
			...themes.bodyMedium,
			color: color.text.dark,
			backgroundColor: color.neutral[1],
			borderBottom: `1px solid ${color.neutral[4]}`,
			paddingTop: '8px',
			paddingBottom: '8px',
		},
		'& .MuiTableCell-body': {
			...themes.bodyRegular,
			color: color.text.dark,
			borderBottom: `1px solid ${color.neutral[4]}`,
			padding: '14px 16px',
		},
	},
	documentName: {
		...themes.bodyMedium,
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	uploadDate: {
		...themes.bodySmall,
		color: `${color.neutral[8]} !important`,
	},
	updatedBy: {
		...themes.bodySmall,
		color: `${color.neutral[8]} !important`,
	},
	actions: {
		display: 'flex',
		justifyContent: 'flex-end',
		alignItems: 'center',
		gap: '2px',
	},
	iconButton: {
		width: '16px !important',
		height: '16px !important',
		color: `${color.primary[90]} !important`,
	},
}));
